/**
 * FamilyDashboardPage — plain-English household financial health.
 *
 * Designed for family members who aren't finance experts.
 * No jargon, no terminal, no technical charts — just clear answers
 * to "how are we doing?"
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Users, TrendingUp, Home, Wallet, PiggyBank, AlertCircle, Target, RefreshCw, Globe, Shield, TrendingDown, ShieldCheck, Banknote, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react'
import { api, getPortfolioHealth, getInsights, getLifeGoals, getFinancialLevers, simulateLevers, getResilienceScore, patchPropertySaleInProgress } from '@/lib/api'
import type { PortfolioHealthResponse, ResilienceScoreResponse, ResilienceComponent } from '@/lib/types'
import type { Insight, LifeGoal, FinancialLever, LeverSimulationResponse, SaleInProgress } from '@/lib/api'
import StatCard from '@/components/ui/StatCard'

// ── Helpers ───────────────────────────────────────────────────────────────────

import { fmtGBP, fmtGBPCompact, fmtNum, fmtPct, fmtSigned } from '@/lib/format'

function gradeColour(grade: string): { bg: string; text: string; ring: string } {
  if (grade === 'A') return { bg: 'bg-emerald-500', text: 'text-emerald-400', ring: 'ring-emerald-500/30' }
  if (grade === 'B') return { bg: 'bg-emerald-400', text: 'text-emerald-300', ring: 'ring-emerald-400/30' }
  if (grade === 'C') return { bg: 'bg-amber-500', text: 'text-amber-400', ring: 'ring-amber-500/30' }
  if (grade === 'D') return { bg: 'bg-amber-600', text: 'text-amber-500', ring: 'ring-amber-600/30' }
  return { bg: 'bg-rose-500', text: 'text-rose-400', ring: 'ring-rose-500/30' }
}

function scoreColour(score: number): string {
  if (score >= 75) return 'text-emerald-600'
  if (score >= 50) return 'text-amber-600'
  return 'text-rose-600'
}

function _topFix(dimension: string): string {
  const fixes: Record<string, string> = {
    concentration: 'Trim over-weight position — target ≤20% of pool',
    crypto_allocation: 'Reduce crypto to ≤20% — rebalance to equities/bonds',
    fx_risk: 'Hedge FX exposure or rebalance into GBP assets',
    diversification: 'Add holdings in under-represented sectors',
    fixed_income: 'Add gilts / bonds — 10-20% of portfolio is typical',
    cash_drag: 'Deploy excess cash — over 10% is a drag on returns',
    risk_adjusted_return: 'Review underperformers against benchmark',
    signal_alignment: 'Holdings misaligned with current signals',
    smart_money_alignment: 'Smart money is fading names you own',
    currency: 'Currency mix is concentrated — diversify',
    fees: 'Fee drag — switch to cheaper equivalent funds',
    tax: 'Tax wrappers sub-optimal — use ISA/SIPP allowances',
    liquidity: 'Too much in illiquid assets',
    momentum: 'Holdings are momentum-negative — review',
  }
  return fixes[dimension] || 'Review this dimension'
}

function progressColour(pct: number): string {
  if (pct >= 75) return 'bg-emerald-500'
  if (pct >= 40) return 'bg-amber-400'
  return 'bg-rose-400'
}

function goalIcon(category: string): string {
  const map: Record<string, string> = {
    retirement: '🏖️',
    property: '🏡',
    education: '🎓',
    travel: '✈️',
    renovation: '🔨',
    car: '🚗',
    wedding: '💍',
    emergency: '🛡️',
  }
  return map[category?.toLowerCase()] ?? '🎯'
}

// ── Lever helpers ─────────────────────────────────────────────────────────────

function LeverIcon({ icon, size = 20 }: { icon: string; size?: number }) {
  switch (icon) {
    case 'home':        return <Home size={size} />
    case 'globe':       return <Globe size={size} />
    case 'piggy-bank':  return <PiggyBank size={size} />
    case 'wallet':      return <Wallet size={size} />
    case 'trending-up': return <TrendingUp size={size} />
    case 'shield':      return <Shield size={size} />
    case 'trending-down': return <TrendingDown size={size} />
    default:            return <Target size={size} />
  }
}

const CATEGORY_COLOURS: Record<string, { bg: string; text: string }> = {
  property:   { bg: 'bg-purple/20',      text: 'text-purple-light' },
  tax:        { bg: 'bg-amber-500/20',   text: 'text-amber-300'  },
  savings:    { bg: 'bg-emerald-500/20', text: 'text-emerald-300'},
  investment: { bg: 'bg-blue-500/20',    text: 'text-blue-300'   },
  pension:    { bg: 'bg-cyan/20',        text: 'text-cyan-light' },
  risk:       { bg: 'bg-rose-500/20',    text: 'text-rose-300'   },
}

const DIFFICULTY_CONFIG = {
  low:    { label: 'Easy',   bg: 'bg-emerald-500/20', text: 'text-emerald-300' },
  medium: { label: 'Medium', bg: 'bg-amber-500/20',   text: 'text-amber-300'  },
  hard:   { label: 'Hard',   bg: 'bg-rose-500/20',    text: 'text-rose-300'   },
}

// ── FireStatus type (minimal subset we need) ──────────────────────────────────

interface FireStatus {
  target_required_gbp: number
  fire_applicable_gbp: number
  total_net_worth_gbp: number
  years_to_fire: number | null
  annual_spend_gbp: number
  assumptions?: {
    planning_assumptions?: {
      /** Per-property sale plans, keyed by holding_id — the real source of sale years */
      property_plans?: Array<{
        holding_id?: string | null
        sell_year?: number | null
        label?: string | null
      }> | null
    }
  }
}

// ── ProgressBar ───────────────────────────────────────────────────────────────
// Uses w-[X%] Tailwind arbitrary classes. These are safelisted in tailwind.config.js
// (pattern /^w-\[([0-9]|[1-9][0-9]|100)%\]$/) so they are always present in the
// bundle even though they're built from runtime values. No style prop needed.

function ProgressBar({ pct, colour }: { pct: number; colour: string }) {
  const clamped = Math.min(100, Math.max(0, Math.round(pct)))
  return (
    <div className={`h-full rounded-full transition-all duration-700 w-[${clamped}%] ${colour}`} />
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card p-5 md:p-6">
      <h2 className="panel-title mb-4">{title}</h2>
      {children}
    </section>
  )
}

type RegimePortfolioImpact = {
  regime_score: number
  regime_direction: 'risk_on' | 'risk_off' | 'neutral'
  supportive_pct: number
  headwind_pct: number
  total_tracked_gbp: number
  archetypes: Array<{
    archetype: string
    pct: number
    impact: 'supportive' | 'headwind' | 'neutral' | 'mixed'
    tickers: string[]
  }>
}

function RegimeImpactSection() {
  const [data, setData] = useState<RegimePortfolioImpact | null>(null)
  const [showDetail, setShowDetail] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/macro/regime/portfolio-impact')
        if (!cancelled && res.data?.archetypes) setData(res.data)
      } catch { /* silent */ }
    })()
    return () => { cancelled = true }
  }, [])

  if (!data || data.archetypes.length === 0) return null

  const top = data.archetypes.slice(0, 6)
  const helpedPct = Math.round(data.supportive_pct ?? 0)
  const hurtPct = Math.round(data.headwind_pct ?? 0)
  const directionWord =
    data.regime_direction === 'risk_on' ? 'favours taking risk' :
    data.regime_direction === 'risk_off' ? 'favours caution' :
    'is broadly neutral'
  return (
    <Section title="Regime Impact on Family Wealth">
      {/* Plain-sentence lead — the one thing to take away */}
      <p className="text-base text-text-primary leading-relaxed">
        Conditions currently help about{' '}
        <span className="font-bold text-emerald-400">{helpedPct}%</span> of what you own
        {hurtPct > 0 && (
          <> and work against about <span className="font-bold text-rose-400">{hurtPct}%</span></>
        )}.
      </p>
      <p className="text-sm text-text-muted mt-1">
        The market backdrop {directionWord} right now.{' '}
        <Link to="/app/regime" className="text-blue-light hover:underline">Full regime view →</Link>
      </p>

      <button
        type="button"
        onClick={() => setShowDetail(v => !v)}
        aria-expanded={showDetail}
        className="mt-2 min-h-[40px] inline-flex items-center px-3 -ml-3 text-sm font-medium text-blue-light hover:underline"
      >
        {showDetail ? 'Hide the detail' : 'Show the detail'}
      </button>

      {showDetail && (
        <div className="mt-2">
          <div className="flex items-center gap-4 mb-4 flex-wrap text-sm">
            <div>
              <span className="text-text-muted">Current regime:</span>{' '}
              <span className={
                data.regime_direction === 'risk_on' ? 'text-emerald-400 font-semibold' :
                data.regime_direction === 'risk_off' ? 'text-rose-400 font-semibold' :
                'text-text-muted font-semibold'
              }>
                {data.regime_direction.replace('_', ' ')}
              </span>
              <span className="ml-1 font-mono text-text-muted">
                ({fmtSigned(data.regime_score, 1)})
              </span>
            </div>
            <div>
              <span className="text-text-muted">Supportive:</span>{' '}
              <span className="text-emerald-400 font-mono">{fmtPct(data.supportive_pct, 1)}</span>
            </div>
            <div>
              <span className="text-text-muted">Headwind:</span>{' '}
              <span className="text-rose-400 font-mono">{fmtPct(data.headwind_pct, 1)}</span>
            </div>
          </div>
          <div className="space-y-2">
            {top.map(a => {
              const colour =
                a.impact === 'supportive' ? 'bg-emerald-500/60' :
                a.impact === 'headwind' ? 'bg-rose-500/60' :
                a.impact === 'mixed' ? 'bg-amber-500/60' : 'bg-text-muted/40'
              const textColour =
                a.impact === 'supportive' ? 'text-emerald-300' :
                a.impact === 'headwind' ? 'text-rose-300' :
                a.impact === 'mixed' ? 'text-amber-300' : 'text-text-muted'
              return (
                <div key={a.archetype} className="flex items-center gap-3 text-sm">
                  <div className="w-36 flex-shrink-0 capitalize">{a.archetype.replace(/_/g, ' ')}</div>
                  <div className="flex-1 bg-surface/50 h-3 rounded overflow-hidden">
                    <div className={colour} style={{ width: `${a.pct}%`, height: '100%' }} />
                  </div>
                  <div className="w-14 text-right font-mono">{fmtPct(a.pct, 1)}</div>
                  <div className={`w-20 text-right uppercase tracking-wider text-xs ${textColour}`}>
                    {a.impact}
                  </div>
                </div>
              )
            })}
          </div>
          <p className="mt-4 text-xs text-text-muted leading-relaxed">
            Archetype impact under current regime. Supportive = tailwind,
            headwind = fighting the regime. Based on household-wide tracked
            holdings (£{(data.total_tracked_gbp ?? 0).toLocaleString('en-GB')}).
          </p>
        </div>
      )}
    </Section>
  )
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
  return <div className={`skeleton ${className ?? 'h-4 w-32'}`} />
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-80" />
        </div>
        <Skeleton className="h-9 w-24 rounded-lg" />
      </div>
      {/* Score section skeleton */}
      <div className="card p-5 space-y-4">
        <Skeleton className="h-5 w-32" />
        <div className="flex gap-6 items-center">
          <Skeleton className="w-32 h-32 rounded-full flex-shrink-0" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      </div>
      {/* Numbers section skeleton */}
      <div className="card p-5 space-y-4">
        <Skeleton className="h-5 w-32" />
        <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Resilience score helpers ──────────────────────────────────────────────────

const RESILIENCE_COMPONENT_LABELS: Record<string, string> = {
  sustainability: 'Retirement Sustainability',
  cash_runway:    'Emergency Cash Runway',
  concentration:  'Concentration Risk',
  fx:             'FX / Currency Exposure',
}

function resilienceStatusColour(status: ResilienceComponent['status']): {
  border: string; bg: string; text: string; bar: string
} {
  if (status === 'good')        return { border: 'border-emerald-500/30', bg: 'bg-emerald-500/5', text: 'text-emerald-400', bar: 'bg-emerald-500' }
  if (status === 'warn')        return { border: 'border-amber-500/30',   bg: 'bg-amber-500/5',   text: 'text-amber-400',   bar: 'bg-amber-400'  }
  if (status === 'bad')         return { border: 'border-rose-500/30',    bg: 'bg-rose-500/5',    text: 'text-rose-400',    bar: 'bg-rose-500'   }
  /* unavailable */              return { border: 'border-border',         bg: 'bg-surface/40',    text: 'text-text-muted',  bar: 'bg-border'     }
}

function ResilienceGradeBadge({ grade }: { grade: ResilienceScoreResponse['grade'] }) {
  const colour =
    grade === 'A' ? 'bg-emerald-500'  :
    grade === 'B' ? 'bg-emerald-400'  :
    grade === 'C' ? 'bg-amber-500'    :
    grade === 'D' ? 'bg-amber-600'    :
    grade === 'F' ? 'bg-rose-500'     :
                    'bg-surface-hover'
  return (
    <div
      className={`flex-shrink-0 w-28 h-28 rounded-full flex flex-col items-center justify-center ${colour} shadow-lg`}
      aria-label={`Resilience score grade ${grade}`}
    >
      <span className="text-4xl font-black text-white leading-none">{grade}</span>
      <span className="text-xs font-semibold text-white/70 mt-0.5">Grade</span>
    </div>
  )
}

function ResilienceScoreSection() {
  const [data, setData] = useState<ResilienceScoreResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getResilienceScore()
        if (!cancelled) setData(res)
      } catch {
        if (!cancelled) setData(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  if (loading) {
    return (
      <Section title="Financial Resilience">
        <div className="space-y-3">
          <div className="flex gap-6 items-center">
            <div className="skeleton w-28 h-28 rounded-full flex-shrink-0" />
            <div className="space-y-2 flex-1">
              <div className="skeleton h-5 w-2/3" />
              <div className="skeleton h-4 w-full" />
              <div className="skeleton h-4 w-3/4" />
            </div>
          </div>
        </div>
      </Section>
    )
  }

  if (!data) {
    return (
      <Section title="Financial Resilience">
        <p className="text-text-muted text-sm italic">Resilience score not available right now.</p>
      </Section>
    )
  }

  // All-None case
  if (data.score == null) {
    return (
      <Section title="Financial Resilience">
        <div className="flex items-start gap-3 bg-surface-hover/50 border border-border rounded-xl px-5 py-4">
          <ShieldCheck size={18} className="text-text-muted flex-shrink-0 mt-0.5" />
          <p className="text-text-muted text-sm">{data.summary}</p>
        </div>
      </Section>
    )
  }

  const scoreColourClass =
    data.score >= 80 ? 'text-emerald-400' :
    data.score >= 65 ? 'text-emerald-300' :
    data.score >= 50 ? 'text-amber-400'   :
    data.score >= 35 ? 'text-amber-500'   :
                       'text-rose-400'

  return (
    <Section title="Financial Resilience">
      {/* Header row: grade circle + score + summary */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-6">
        <ResilienceGradeBadge grade={data.grade} />
        <div className="text-center sm:text-left space-y-1.5">
          <p className="text-xl font-bold text-text-primary">
            Resilience score{' '}
            <span className={`font-black text-2xl ${scoreColourClass}`}>{data.score}</span>
            <span className="text-text-muted text-base font-normal"> / 100</span>
          </p>
          {data.is_demo && (
            <span className="inline-block text-[10px] uppercase tracking-wider bg-surface-hover text-text-muted px-2 py-0.5 rounded-full">
              Illustrative
            </span>
          )}
          <p className="text-text-secondary text-sm leading-relaxed max-w-lg">{data.summary}</p>
        </div>
      </div>

      {/* Component breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 stagger-children">
        {(data.components ?? []).map((comp) => {
          const label = RESILIENCE_COMPONENT_LABELS[comp.name] ?? comp.name
          const col = resilienceStatusColour(comp.status)
          const barPct = comp.sub_score_0_1 != null ? Math.round(comp.sub_score_0_1 * 100) : 0

          return (
            <div
              key={comp.name}
              className={`rounded-xl border p-3.5 ${col.border} ${col.bg}`}
              aria-label={`${label}: ${comp.available ? `${barPct}%` : 'unavailable'}`}
            >
              {/* Label row */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider truncate">
                  {label}
                </span>
                {comp.available ? (
                  <span className={`text-sm font-bold font-mono ${col.text}`}>
                    {barPct}%
                  </span>
                ) : (
                  <span className="text-xs text-text-muted italic">n/a</span>
                )}
              </div>

              {/* Progress bar — uses safelisted w-[X%] Tailwind class (no inline style) */}
              <div className="h-1.5 rounded-full bg-surface-hover overflow-hidden mb-2">
                {comp.available && (
                  <ProgressBar pct={Math.max(barPct, 3)} colour={col.bar} />
                )}
              </div>

              {/* Detail */}
              <p className="text-xs text-text-muted leading-snug">{comp.detail}</p>

              {/* Contribution badge */}
              {comp.score_contribution != null && (
                <p className="text-[11px] text-text-muted mt-1.5">
                  Contributes{' '}
                  <span className="font-mono font-semibold text-text-secondary">
                    {fmtNum(comp.score_contribution, 1)} pts
                  </span>{' '}
                  (weight {fmtNum((comp.weight_used ?? NaN) * 100, 0)}%)
                </p>
              )}
            </div>
          )
        })}
      </div>

      {/* Methodology note */}
      <p className="mt-4 text-[11px] text-text-muted leading-relaxed">
        {data.methodology}
      </p>
    </Section>
  )
}

// ── Income Coverage types ─────────────────────────────────────────────────────

interface CoverageDetail {
  coverage_ratio: number | null
  coverage_pct: number | null
  surplus_gbp: number
  gap_gbp: number
  status: 'covered' | 'tight' | 'shortfall' | 'no_estimate'
  months_covered: number | null
  note: string
}

interface IncomeCoverageData {
  coverage: CoverageDetail
  with_speculative: CoverageDetail
  components: {
    dividend_reliable_gbp: number
    dividend_total_gbp: number
    pension_gbp: number
    expenses_gbp: number
    dividend_available: boolean
    pension_available: boolean
    expenses_available: boolean
  }
  reliable_income_gbp: number
  speculative_income_gbp: number
  total_income_gbp: number
  annual_expenses_gbp: number
  is_demo: boolean
  note: string
}

// ── IncomeCoverageCard ────────────────────────────────────────────────────────

function IncomeCoverageCard() {
  const [data, setData] = useState<IncomeCoverageData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get<IncomeCoverageData>('/api/income/coverage')
        if (!cancelled && res.data) setData(res.data)
      } catch {
        // Silent — missing coverage is non-fatal on this dashboard
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Status styling config
  function statusConfig(status: CoverageDetail['status'] | undefined) {
    switch (status) {
      case 'covered':
        return {
          bg:        'bg-emerald-500/10',
          border:    'border-emerald-500/30',
          text:      'text-emerald-400',
          badgeBg:   'bg-emerald-500/20',
          label:     'Covered',
          Icon:      CheckCircle2,
          barColour: 'bg-emerald-500',
        }
      case 'tight':
        return {
          bg:        'bg-amber-500/10',
          border:    'border-amber-500/30',
          text:      'text-amber-400',
          badgeBg:   'bg-amber-500/20',
          label:     'Tight',
          Icon:      AlertTriangle,
          barColour: 'bg-amber-400',
        }
      case 'shortfall':
        return {
          bg:        'bg-rose-500/10',
          border:    'border-rose-500/30',
          text:      'text-rose-400',
          badgeBg:   'bg-rose-500/20',
          label:     'Shortfall',
          Icon:      XCircle,
          barColour: 'bg-rose-500',
        }
      default:
        return {
          bg:        'bg-surface',
          border:    'border-border',
          text:      'text-text-muted',
          badgeBg:   'bg-surface-hover',
          label:     'No estimate',
          Icon:      AlertCircle,
          barColour: 'bg-text-muted/50',
        }
    }
  }

  if (loading) {
    return (
      <Section title="Income Coverage — Can We Live Off This?">
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
        </div>
      </Section>
    )
  }

  if (!data) return null

  const cov     = data.coverage
  const specc   = data.with_speculative
  const comp    = data.components
  const cfg     = statusConfig(cov.status)
  const StatusIcon = cfg.Icon
  const barPct  = Math.min(100, Math.max(0, cov.coverage_pct ?? 0))
  const specPct = Math.min(100, Math.max(0, specc.coverage_pct ?? 0))
  const noEstimate = cov.status === 'no_estimate'

  return (
    <Section title="Income Coverage — Can We Live Off This?">

      {/* ── Headline block ─────────────────────────────────────────────────── */}
      <div className={`rounded-xl border p-5 mb-5 ${cfg.bg} ${cfg.border}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">

          {/* Big % + status badge */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className={`w-20 h-20 rounded-full flex flex-col items-center justify-center ring-4 ring-border ${cfg.bg}`}>
              <span className={`text-2xl font-black leading-none ${cfg.text}`}>
                {noEstimate ? '—' : `${Math.round(barPct)}%`}
              </span>
              <span className="text-[10px] text-text-muted mt-0.5">coverage</span>
            </div>
            <div>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${cfg.badgeBg} ${cfg.text} mb-1`}>
                <StatusIcon size={12} />
                {cfg.label}
              </div>
              {!noEstimate && (
                <p className="text-sm text-text-muted mt-0.5">
                  {cov.status === 'covered'
                    ? `£${(cov.surplus_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })} surplus/yr`
                    : `£${(cov.gap_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })} gap/yr from capital`
                  }
                </p>
              )}
            </div>
          </div>

          {/* Bar chart column */}
          {!noEstimate && (
            <div className="flex-1 space-y-2 min-w-0">
              {/* Reliable income bar */}
              <div>
                <div className="flex justify-between text-[11px] text-text-muted mb-1">
                  <span>Reliable income</span>
                  <span className="font-mono">{fmtGBPCompact(data.reliable_income_gbp)}/yr</span>
                </div>
                <div className="h-3 bg-surface rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${cfg.barColour}`}
                    style={{ width: `${barPct}%` }}
                  />
                </div>
              </div>
              {/* With speculative bar (only if speculative > 0) */}
              {data.speculative_income_gbp > 0 && (
                <div>
                  <div className="flex justify-between text-[11px] text-text-muted mb-1">
                    <span>+ Speculative dividends</span>
                    <span className="font-mono">{fmtGBPCompact(data.total_income_gbp)}/yr</span>
                  </div>
                  <div className="h-3 bg-surface rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700 bg-blue/50"
                      style={{ width: `${specPct}%` }}
                    />
                  </div>
                </div>
              )}
              {/* Expenses reference line label */}
              <p className="text-[11px] text-text-muted">
                Estimated spend: <span className="font-mono font-semibold text-text-secondary">{fmtGBPCompact(data.annual_expenses_gbp)}/yr</span>
                {cov.months_covered != null && (
                  <span className="ml-2">
                    · reliable income covers{' '}
                    <span className="font-semibold text-text-secondary">
                      {fmtNum(cov.months_covered, 1)} months
                    </span>
                    {' '}of spend
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Component breakdown ────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {/* Dividends */}
        <div className="bg-surface rounded-xl px-4 py-3 border border-border">
          <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
            <Banknote size={11} /> Dividends (reliable)
          </p>
          {comp.dividend_available ? (
            <>
              <p className="font-semibold text-text-primary">{fmtGBPCompact(comp.dividend_reliable_gbp)}/yr</p>
              {comp.dividend_total_gbp > comp.dividend_reliable_gbp && (
                <p className="text-[11px] text-text-muted mt-0.5">
                  +{fmtGBPCompact(comp.dividend_total_gbp - comp.dividend_reliable_gbp)} speculative
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-text-muted italic">Unavailable</p>
          )}
        </div>

        {/* Pensions */}
        <div className="bg-surface rounded-xl px-4 py-3 border border-border">
          <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
            <PiggyBank size={11} /> Pensions (state + DB)
          </p>
          {comp.pension_available ? (
            <p className="font-semibold text-text-primary">{fmtGBPCompact(comp.pension_gbp)}/yr</p>
          ) : (
            <p className="text-xs text-text-muted italic">Unavailable</p>
          )}
        </div>

        {/* Expenses */}
        <div className="bg-surface rounded-xl px-4 py-3 border border-border">
          <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
            <Wallet size={11} /> Annual Spend
          </p>
          {comp.expenses_available ? (
            <p className="font-semibold text-text-primary">{fmtGBPCompact(comp.expenses_gbp)}/yr</p>
          ) : (
            <p className="text-xs text-text-muted italic">Not set in profile</p>
          )}
        </div>
      </div>

      {/* ── Honest note ───────────────────────────────────────────────────── */}
      <p className="text-xs text-text-muted leading-relaxed">
        {data.note}
        {!noEstimate && cov.status !== 'covered' && (
          <span>
            {' '}
            <Link to="/app/drawdown" className="text-blue-light hover:underline">
              Drawdown Planner →
            </Link>
          </span>
        )}
      </p>
      {data.is_demo && (
        <p className="mt-2 text-[11px] text-text-muted italic">
          Illustrative — based on demo portfolio.
        </p>
      )}
      <p className="mt-2 text-[11px] text-text-muted">
        Reliable income = high-confidence dividends (4+ payments, known frequency) + guaranteed pensions.
        A shortfall means drawing from capital — not insolvency. See the{' '}
        <Link to="/app/drawdown" className="text-blue-light hover:underline">Drawdown Planner</Link>
        {' '}for a sustainable withdrawal strategy.
      </p>
    </Section>
  )
}

// ── Sale-in-progress card body ────────────────────────────────────────────────
// Shown INSIDE a property lever when a REAL sale is agreed (e.g. 35 Farm
// Avenue @ £610k, 28 Jul 2026). Replaces the hypothetical slider with the
// agreed figures + clearly-labelled editable cost estimates + a completion
// date field. Costs save back to the platform so every planner follows.

function SaleAgreedDetails({
  slug,
  sale,
  onSaved,
}: {
  slug: string
  sale: SaleInProgress
  onSaved: () => void
}) {
  const [agentFeePct, setAgentFeePct] = useState<number>(sale.sale_costs.agent_fee_pct)
  const [conveyancing, setConveyancing] = useState<number>(sale.sale_costs.conveyancing_gbp)
  const [completion, setCompletion] = useState<string>(sale.expected_completion ?? '')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const dirty =
    agentFeePct !== sale.sale_costs.agent_fee_pct ||
    conveyancing !== sale.sale_costs.conveyancing_gbp ||
    (completion || null) !== (sale.expected_completion ?? null)

  async function save() {
    setSaving(true)
    setSaveError(null)
    try {
      await patchPropertySaleInProgress(slug, {
        agent_fee_pct: agentFeePct,
        conveyancing_gbp: conveyancing,
        ...(completion ? { expected_completion: completion } : {}),
      })
      onSaved()
    } catch {
      setSaveError('Could not save — try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-2 pt-2 border-t border-border/50" onClick={e => e.stopPropagation()}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <span className="text-text-muted">Agreed price</span>
        <span className="font-mono text-right text-text-primary">{fmtGBP(sale.sale_agreed_price_gbp)}</span>
        <span className="text-text-muted">Est. fees (editable)</span>
        <span className="font-mono text-right text-text-primary">−{fmtGBP(sale.sale_costs.total_gbp)}</span>
        <span className="text-text-muted">Est. CGT</span>
        <span className="font-mono text-right text-text-primary">
          {sale.cgt_known && sale.cgt ? `−${fmtGBP(sale.cgt.cgt_due_gbp)}` : '—'}
        </span>
        {sale.mortgage_outstanding_gbp > 0 && (
          <>
            <span className="text-text-muted">Mortgage cleared</span>
            <span className="font-mono text-right text-text-primary">−{fmtGBP(sale.mortgage_outstanding_gbp)}</span>
          </>
        )}
        <span className="text-text-secondary font-medium">Cash on completion</span>
        <span className="font-mono text-right font-semibold text-emerald-300">{fmtGBP(sale.cash_after_mortgage_gbp)}</span>
      </div>
      {!sale.cgt_known && (
        <p className="text-[11px] text-amber-400 flex items-start gap-1">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" aria-hidden />
          {sale.cgt_caveat ?? 'CGT unknown — purchase price needed'} — CGT is NOT included in this figure.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-text-muted">Agent fee % (+VAT)</span>
          <input
            type="number"
            value={agentFeePct}
            min={0}
            step={0.05}
            onChange={e => setAgentFeePct(parseFloat(e.target.value) || 0)}
            className="input mt-0.5 px-2 py-1 text-sm font-mono"
            aria-label="Estate agent fee percent (estimate)"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-text-muted">Conveyancing £</span>
          <input
            type="number"
            value={conveyancing}
            min={0}
            step={100}
            onChange={e => setConveyancing(parseFloat(e.target.value) || 0)}
            className="input mt-0.5 px-2 py-1 text-sm font-mono"
            aria-label="Conveyancing cost estimate"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-text-muted">Completion date</span>
          <input
            type="date"
            value={completion}
            onChange={e => setCompletion(e.target.value)}
            className="input mt-0.5 px-2 py-1 text-sm font-mono"
            aria-label="Expected completion date"
          />
        </label>
      </div>
      <div className="flex items-center gap-2">
        {dirty && (
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-primary min-h-[32px] px-3 py-1 text-xs"
          >
            {saving ? 'Saving…' : 'Save estimates'}
          </button>
        )}
        {saveError && <span className="text-[11px] text-rose-400">{saveError}</span>}
        <span className="text-[10px] text-text-muted">
          Estimates — the final completion statement will differ.
        </span>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function FamilyDashboardPage() {
  const [health, setHealth]       = useState<PortfolioHealthResponse | null>(null)
  const [fire, setFire]           = useState<FireStatus | null>(null)
  const [summary, setSummary]     = useState<any>(null)
  const [insights, setInsights]   = useState<Insight[]>([])
  const [goals, setGoals]         = useState<LifeGoal[]>([])
  const [levers, setLevers]       = useState<FinancialLever[]>([])
  const [selectedLevers, setSelectedLevers] = useState<Set<string>>(new Set())
  // Per-lever timing in years-from-now (only honoured for downsize / sell_farm_avenue today)
  const [leverTiming, setLeverTiming] = useState<Record<string, number>>({})
  const [simulation, setSimulation] = useState<LeverSimulationResponse | null>(null)
  const [simRunning, setSimRunning] = useState(false)
  const [simError, setSimError] = useState<string | null>(null)
  const [loading, setLoading]     = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [slowLoad, setSlowLoad]   = useState(false)
  const [dataError, setDataError] = useState<string | null>(null)
  const [trendPct30d, setTrendPct30d] = useState<number | null>(null)
  const [sectorBreakdown, setSectorBreakdown] = useState<
    {
      sectors: Array<{ sector: string; value_gbp: number; pct: number }>
      total_gbp: number
      coverage?: {
        with_lookthrough: number
        with_raw_sector: number
        unclassified: number
      }
    } | null
  >(null)

  async function loadAll() {
    setDataError(null)
    // Show "taking longer than expected" after 8 seconds
    const slowTimer = setTimeout(() => setSlowLoad(true), 8_000)
    try {
      const [healthRes, insightsRes, goalsRes, summaryRes] = await Promise.allSettled([
        getPortfolioHealth(),
        getInsights(),
        getLifeGoals(),
        api.get('/api/accounts/summary'),
      ])

      if (healthRes.status === 'fulfilled') setHealth(healthRes.value)
      if (insightsRes.status === 'fulfilled') setInsights((insightsRes.value.insights ?? []).filter(i => !i.dismissed).slice(0, 4))
      // Backend stores status lowercase ('active') — compare case-insensitively
      if (goalsRes.status === 'fulfilled') setGoals(goalsRes.value.filter(g => g.status?.toUpperCase() === 'ACTIVE'))
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data)

      // Show a top-level warning if all core requests failed
      const coreFailures = [healthRes, summaryRes].filter(r => r.status === 'rejected').length
      if (coreFailures === 2) {
        setDataError('Could not load account data. Check your connection and try again.')
      }

      // Fetch FIRE status and financial levers (may not be available on all sessions)
      await Promise.allSettled([
        api.get<FireStatus>('/api/planning/fire').then(r => setFire(r.data)).catch(() => {}),
        getFinancialLevers().then(r => setLevers(r.levers)).catch(() => {}),
      ])
    } catch {
      setDataError('Something went wrong loading the dashboard. Please try again.')
    } finally {
      clearTimeout(slowTimer)
      setSlowLoad(false)
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => { loadAll() }, [])

  // Seed sale-timing sliders from the household's saved planning
  // assumptions — so toggling a lever defaults to the actual planned
  // sale year rather than "now". User edits to a slider win once made.
  useEffect(() => {
    const pa = fire?.assumptions?.planning_assumptions
    if (!pa) return
    const seed = (id: string, year: number | null | undefined) => {
      if (!year) return
      const yearsFromNow = Math.max(0, year - new Date().getFullYear())
      if (yearsFromNow > 20) return
      setLeverTiming(prev => (
        prev[id] !== undefined ? prev : { ...prev, [id]: yearsFromNow }
      ))
    }
    // Real sale years live in property_plans[], keyed by holding_id —
    // the lever ids are "sell_<holding_id>" (matches the backend lever loop).
    for (const p of pa.property_plans ?? []) {
      if (p?.holding_id) seed(`sell_${p.holding_id}`, p.sell_year)
    }
  }, [fire])

  // 30-day net-worth trend for the StatCard badge
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/reports/performance-series?days=30')
        const rows = Array.isArray(res.data) ? res.data : (res.data?.series ?? [])
        if (cancelled) return
        const vals = rows
          .filter((r: any) => r && r.portfolio_value != null)
          .map((r: any) => Number(r.portfolio_value))
        if (vals.length >= 2) {
          const first = vals[0]
          const last = vals[vals.length - 1]
          if (first > 0) {
            setTrendPct30d(((last - first) / first) * 100)
          }
        }
      } catch {
        if (!cancelled) setTrendPct30d(null)
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Sector breakdown — load independently from loadAll since it can
  // be slow and we don't want to block the main dashboard render
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/sectors/portfolio-breakdown')
        if (!cancelled) setSectorBreakdown(res.data)
      } catch {
        if (!cancelled) setSectorBreakdown(null)
      }
    })()
    return () => { cancelled = true }
  }, [])

  function handleRefresh() {
    setRefreshing(true)
    loadAll()
  }

  // ── Loading state ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div>
        <DashboardSkeleton />
        {slowLoad && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-5 py-3 shadow-lg text-sm text-amber-300 font-medium">
            Loading is taking longer than expected…
          </div>
        )}
      </div>
    )
  }

  // ── Derived values ────────────────────────────────────────────────────────

  const netWorth     = summary?.total_net_worth ?? 0
  const tracked      = summary?.tracked_investments_gbp ?? 0
  const propertyNet  = summary?.property_net_equity_gbp ?? 0

  // Income / spend / savings — from summary breakdown (use tracked investments as proxy for monthly figures)
  // Monthly income / spend are not in the summary endpoint — show placeholders if not available
  const monthlyIncome  = 0    // not in summary; left as placeholder
  const monthlySpend   = 0
  const monthlySavings = 0

  const score        = health?.score ?? 0
  const grade        = health?.grade ?? 'C'
  const colours      = gradeColour(grade)

  const fireTarget   = fire?.target_required_gbp ?? 0
  const fireCurrent  = fire?.fire_applicable_gbp ?? tracked
  const firePct      = fireTarget > 0 ? Math.min(100, Math.round((fireCurrent / fireTarget) * 100)) : 0
  const yearsToFire  = fire?.years_to_fire
  const annualSpend  = fire?.annual_spend_gbp ?? 0

  const topInsights  = insights.filter(i => i.priority === 'high' || i.priority === 'medium').slice(0, 4)

  return (
    <div className="space-y-6 animate-fade-in">

      {/* ── New-user pointer to the plain-English guide ──────────────────────── */}
      <Link
        to="/app/guide"
        className="flex items-center justify-between gap-3 bg-blue-500/[0.06] border border-blue-500/20 rounded-xl px-4 py-3 hover:bg-blue-500/10 transition-colors"
      >
        <span className="text-sm text-text-secondary">
          <span className="font-semibold text-text-primary">New to FinanceGenie?</span>{' '}
          A plain-English walkthrough of the main things you can do.
        </span>
        <span className="text-xs text-blue-light font-medium shrink-0">Getting Started →</span>
      </Link>

      {/* ── Top-level data error banner ──────────────────────────────────────── */}
      {dataError && (
        <div className="flex items-start gap-3 bg-rose-500/10 border border-rose-500/20 rounded-xl px-5 py-4">
          <AlertCircle size={18} className="text-rose-500 flex-shrink-0 mt-0.5" aria-hidden />
          <div className="flex-1">
            <p className="text-rose-300 font-medium text-sm">{dataError}</p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            className="min-h-[40px] px-2 text-rose-300 text-sm font-medium hover:underline flex-shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users size={20} className="text-blue" aria-hidden />
            <h1 className="page-title">Family Dashboard</h1>
          </div>
          <p className="text-sm text-text-secondary">A clear picture of how your household is doing — no jargon, just the facts.</p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="btn-secondary min-h-[40px]"
          aria-label="Refresh data"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </header>

      {/* fall-through */}
      {/* ── 1. Household Health Score ─────────────────────────────────────────── */}
      <Section title="How Are We Doing?">
        {/* When the health fetch failed there IS no score — rendering the
            circle would show a fabricated "0/100 Grade C". Skip it. */}
        {health ? (
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          {/* Big score circle */}
          <div
            className={`flex-shrink-0 w-32 h-32 rounded-full flex flex-col items-center justify-center
                        ring-8 ${colours.ring} ${colours.bg} shadow-lg`}
            aria-label={`Health score ${score} out of 100`}
          >
            <span className="stat-hero text-white">{score}</span>
            <span className="text-sm font-bold text-white/80">/ 100</span>
          </div>

          {/* Copy */}
          <div className="text-center sm:text-left space-y-2">
            <p className="text-2xl font-bold text-text-primary">
              Your family finances score{' '}
              <span className={scoreColour(score)}>{score} out of 100</span>
              {' '}— Grade <span className={colours.text}>{grade}</span>
            </p>
            {health?.summary && (
              <p className="text-text-muted text-base leading-relaxed max-w-lg">{health.summary}</p>
            )}
          </div>
        </div>
        ) : (
          <p className="text-text-muted text-sm italic">Health score unavailable right now.</p>
        )}

        {/* Sub-score breakdown — shows WHY the score is what it is */}
        {health?.breakdown && typeof health.breakdown === 'object' && Object.keys(health.breakdown).length > 0 && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 stagger-children">
            {Object.entries(health.breakdown as Record<string, { score: number; max: number; note: string }>).map(([key, d]) => {
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
              const pct = d.max > 0 ? d.score / d.max : 0
              const healthy = pct >= 0.8
              const warn = pct >= 0.5 && pct < 0.8
              const bad = pct < 0.5
              return (
                <div key={key} className={[
                  'rounded-xl border p-3',
                  healthy ? 'border-emerald-500/30 bg-emerald-500/5' :
                  warn ? 'border-amber-500/30 bg-amber-500/5' :
                  'border-rose-500/30 bg-rose-500/5',
                ].join(' ')}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">{label}</span>
                    <span className={[
                      'text-sm font-bold font-mono',
                      healthy ? 'text-emerald-500' : warn ? 'text-amber-500' : 'text-rose-500',
                    ].join(' ')}>
                      {d.score}<span className="text-text-muted text-xs">/{d.max}</span>
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-surface-hover overflow-hidden mb-2">
                    <div className={[
                      'h-full rounded-full',
                      healthy ? 'bg-emerald-500' : warn ? 'bg-amber-500' : 'bg-rose-500',
                    ].join(' ')} style={{ width: `${Math.max(4, pct * 100)}%` }} />
                  </div>
                  <p className="text-xs text-text-muted leading-snug">{d.note}</p>
                  {bad && (
                    <p className="text-[11px] font-medium text-rose-400 mt-1.5">
                      {_topFix(key)}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Section>

      {/* ── 2. Are We On Track? (FIRE) ───────────────────────────────────────── */}
      {fireTarget === 0 && !loading && (
        <Section title="Are We On Track to Retire?">
          <p className="text-text-muted text-sm italic">
            Retirement tracking not yet set up. Visit the <span className="font-medium text-text-primary">Planning</span> page to configure your FIRE target and annual spending.
          </p>
        </Section>
      )}
      {fireTarget > 0 && (
        <Section title="Are We On Track to Retire?">
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-xl font-semibold text-text-primary">
                You need{' '}
                <span className="text-blue-600 font-bold">{fmtGBPCompact(fireTarget)}</span>
                {annualSpend > 0 && (
                  <> to retire on {fmtGBPCompact(annualSpend)}/year</>
                )}
              </p>
              <p className="text-text-secondary text-base">
                You currently have{' '}
                <span className="font-semibold text-text-primary">{fmtGBPCompact(fireCurrent)}</span>{' '}
                in investable assets — that's{' '}
                <span className={`font-bold ${firePct >= 75 ? 'text-emerald-600' : firePct >= 40 ? 'text-amber-600' : 'text-rose-600'}`}>
                  {firePct}% of the way there
                </span>
              </p>
            </div>

            {/* Progress bar */}
            <div className="space-y-1">
              <div
                className="relative h-6 bg-surface-hover rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={firePct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Retirement progress: ${firePct}%`}
              >
                <ProgressBar pct={firePct} colour={progressColour(firePct)} />
                {firePct > 10 && (
                  <span className="absolute inset-0 flex items-center pl-3 text-white text-sm font-bold drop-shadow">
                    {firePct}%
                  </span>
                )}
              </div>
              <div className="flex justify-between text-xs text-text-muted">
                <span>£0</span>
                <span>{fmtGBPCompact(fireTarget)}</span>
              </div>
            </div>

            {/* Years callout */}
            {yearsToFire !== null && yearsToFire !== undefined && yearsToFire > 0 && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl px-5 py-4">
                <p className="text-blue-300 font-semibold text-base">
                  At your current savings rate, you'll reach this in about{' '}
                  <span className="text-blue-600 font-bold text-xl">{Math.round(yearsToFire)} years</span>
                </p>
              </div>
            )}
            {yearsToFire !== null && yearsToFire !== undefined && yearsToFire <= 0 && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-5 py-4">
                <p className="text-emerald-300 font-semibold text-base">
                  You've already hit your retirement target — you could retire today.
                </p>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* ── 2b. Financial Resilience Score ───────────────────────────────────── */}
      <ResilienceScoreSection />

      {/* ── 3. Key Numbers ───────────────────────────────────────────────────── */}
      <Section title="The Big Numbers">
        <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-4 stagger-children">
          <StatCard
            icon={Wallet}
            label="Total Net Worth"
            value={netWorth > 0 ? fmtGBP(netWorth) : '—'}
            sub={propertyNet > 0 ? `Incl. ${fmtGBPCompact(propertyNet)} property equity` : undefined}
            change={trendPct30d ?? undefined}
            trendLabel="30d"
          />
          <StatCard
            icon={Home}
            label="Investments"
            value={tracked > 0 ? fmtGBP(tracked) : '—'}
            sub={tracked > 0 ? 'Stocks, funds, pensions, ISAs' : undefined}
          />
          {monthlyIncome > 0 && (
            <StatCard
              icon={TrendingUp}
              label="Monthly Income"
              value={fmtGBP(monthlyIncome)}
            />
          )}
          {monthlySavings > 0 && (
            <StatCard
              icon={PiggyBank}
              label="Monthly Savings"
              value={fmtGBP(monthlySavings)}
              sub="What you're adding each month"
            />
          )}
        </div>

        {/* Summary-level net worth breakdown */}
        {summary && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {summary.property_gross_gbp > 0 && (
              <div className="bg-surface rounded-lg px-4 py-3 border border-border">
                <p className="text-xs text-text-muted mb-1">Property (gross)</p>
                <p className="font-semibold text-text-primary">{fmtGBPCompact(summary.property_gross_gbp)}</p>
              </div>
            )}
            {(summary.accessible_cash_gbp ?? summary.external_cash_gbp ?? 0) > 0 && (
              <div className="bg-emerald-500/5 rounded-lg px-4 py-3 border border-emerald-500/20">
                <p className="text-xs text-emerald-300 mb-1">Accessible Cash</p>
                <p className="font-semibold text-text-primary">
                  {fmtGBPCompact(summary.accessible_cash_gbp ?? summary.external_cash_gbp ?? 0)}
                </p>
                <p className="text-[10px] text-text-muted mt-0.5">Bank / savings / premium bonds</p>
              </div>
            )}
            {(summary.wrapper_cash_gbp ?? 0) > 0 && (
              <div className="bg-amber-500/5 rounded-lg px-4 py-3 border border-amber-500/20">
                <p className="text-xs text-amber-300 mb-1">Wrapper Cash (uninvested)</p>
                <p className="font-semibold text-text-primary">{fmtGBPCompact(summary.wrapper_cash_gbp ?? 0)}</p>
                <p className="text-[10px] text-text-muted mt-0.5">Sitting in ISA/SIPP/GIA — should be deployed</p>
              </div>
            )}
            {summary.liabilities_gbp < 0 && (
              <div className="bg-rose-500/10 rounded-lg px-4 py-3 border border-rose-500/20">
                <p className="text-xs text-rose-400 mb-1">Mortgages &amp; Debts</p>
                <p className="font-semibold text-rose-300">{fmtGBPCompact(Math.abs(summary.liabilities_gbp))}</p>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ── 3b. Sector Exposure ─────────────────────────────────────────────
           Decomposes every ETF into its underlying GICS sector weights so
           you see REAL sector tilt, not just "UK Equity / EM Equity" tags.
           Sourced from /api/sectors/portfolio-breakdown. */}
      {sectorBreakdown && sectorBreakdown.sectors.length > 0 && (
        <Section title="Sector Exposure (look-through)">
          <div className="space-y-2">
            {sectorBreakdown.sectors.slice(0, 12).map(s => {
              const isEquity = ![
                'Fixed Income', 'Commodities', 'Alternatives', 'Cash', 'Real Estate',
              ].includes(s.sector)
              return (
                <div key={s.sector} className="flex items-center gap-3">
                  <div className="w-32 md:w-44 text-xs text-text-secondary leading-tight">
                    {s.sector}
                  </div>
                  <div className="flex-1 h-5 bg-surface rounded overflow-hidden relative">
                    <div
                      className={isEquity ? 'bg-blue/60 h-full' : 'bg-amber-500/50 h-full'}
                      style={{ width: `${Math.min(100, s.pct)}%` }}
                    />
                  </div>
                  <div className="w-32 text-right text-xs tabular-nums">
                    <span className="text-text-primary font-semibold">{fmtPct(s.pct, 1)}</span>
                    <span className="text-text-muted ml-1.5">
                      {fmtGBPCompact(s.value_gbp)}
                    </span>
                  </div>
                </div>
              )
            })}
            <p className="text-[10px] text-text-muted mt-2 leading-tight">
              Blue bars = GICS equity sectors · Amber = fixed income / commodities / cash.
              Values decomposed from every ETF's underlying weights (S&P 500,
              FTSE 100, EM IMI, etc) not just the holding's label.
              {sectorBreakdown.coverage && (
                <span className="block mt-0.5">
                  Coverage: {sectorBreakdown.coverage.with_lookthrough} ETF look-through
                  · {sectorBreakdown.coverage.with_raw_sector} raw sector tag
                  · {sectorBreakdown.coverage.unclassified} unclassified
                </span>
              )}
            </p>
          </div>
        </Section>
      )}

      {/* Regime Impact UNMOUNTED from this page (2026-08-08). It was the
          densest jargon block on a surface whose own header promises "no
          jargon" — "Current regime: risk off (-3.4)", Supportive/Headwind
          columns, raw archetype enums — and its only action was a link to a
          pro page. The component stays defined for /app/regime-minded
          surfaces; a family page asks "are we okay?", not "what is the
          macro regime doing to long_duration_bonds". */}

      {/* ── 3d. Income Coverage — "Can we live off this?" ─────────────────── */}
      <IncomeCoverageCard />

      {/* ── 4. Things to Know ────────────────────────────────────────────────── */}
      {topInsights.length > 0 && (
        <Section title="Things to Know">
          <ul className="space-y-3" role="list">
            {topInsights.map(ins => (
              <li
                key={ins.id}
                className={`flex items-start gap-4 rounded-xl px-5 py-4 border ${
                  ins.priority === 'high'
                    ? 'bg-amber-500/10 border-amber-500/20'
                    : 'bg-surface border-border'
                }`}
              >
                <AlertCircle
                  size={18}
                  className={`mt-0.5 flex-shrink-0 ${ins.priority === 'high' ? 'text-amber-500' : 'text-text-muted'}`}
                  aria-hidden
                />
                <div>
                  <p className="font-semibold text-text-primary text-sm">{ins.title}</p>
                  <p className="text-text-secondary text-sm mt-0.5">{ins.message}</p>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* ── 5. Goals ─────────────────────────────────────────────────────────── */}
      {goals.length > 0 && (
        <Section title="Your Goals">
          <ul className="space-y-5" role="list">
            {goals.map(goal => {
              const pct = goal.target_amount > 0
                ? Math.min(100, Math.round((goal.current_saved / goal.target_amount) * 100))
                : 0
              const remaining = Math.max(0, goal.target_amount - goal.current_saved)
              // Backend emits monthly_savings_needed_gbp; the un-suffixed
              // field never exists on the wire — read the _gbp one with fallback
              const monthlyNeeded = goal.monthly_savings_needed ?? goal.monthly_savings_needed_gbp ?? null
              return (
                <li key={goal.id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xl" aria-hidden>{goalIcon(goal.category)}</span>
                      <div>
                        <p className="font-semibold text-text-primary text-sm">{goal.name}</p>
                        {goal.target_date && (
                          <p className="text-xs text-text-muted">
                            Target: {new Date(goal.target_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-text-primary text-sm">{pct}%</p>
                      <p className="text-xs text-text-muted">{fmtGBPCompact(goal.current_saved)} of {fmtGBPCompact(goal.target_amount)}</p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div
                    className="h-3 bg-surface-hover rounded-full overflow-hidden"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${goal.name}: ${pct}% complete`}
                  >
                    <ProgressBar pct={pct} colour={progressColour(pct)} />
                  </div>

                  {/* Supporting text */}
                  <div className="flex justify-between mt-1">
                    {remaining > 0
                      ? <p className="text-xs text-text-muted">{fmtGBPCompact(remaining)} still needed</p>
                      : <p className="text-xs text-emerald-600 font-medium">Goal reached!</p>
                    }
                    {monthlyNeeded != null && monthlyNeeded > 0 && (
                      <p className="text-xs text-text-muted">
                        <Target size={10} className="inline mr-0.5" />
                        {fmtGBPCompact(monthlyNeeded)}/mo needed
                      </p>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </Section>
      )}

      {/* ── 6. What Could Help? ──────────────────────────────────────────────── */}
      {levers.length > 0 && (
        <Section title="What Could Help?">
          <p className="text-text-secondary text-sm mb-5">
            Personalised levers that could meaningfully improve your household's financial position — ranked by impact.
            Tick any combination to simulate the cumulative FIRE-timeline effect below.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            {levers.map(lever => {
              const catColour  = CATEGORY_COLOURS[lever.category] ?? { bg: 'bg-surface-hover', text: 'text-text-secondary' }
              const diffConfig = DIFFICULTY_CONFIG[lever.difficulty] ?? DIFFICULTY_CONFIG.medium
              const hasImpactGbp   = lever.impact_gbp > 0
              const hasImpactYears = lever.impact_years != null && lever.impact_years > 0
              const isSelected = selectedLevers.has(lever.id)

              return (
                <div
                  key={lever.id}
                  className={`p-5 flex flex-col gap-3 transition-all cursor-pointer ${
                    isSelected ? 'card border-blue-400 shadow-md ring-1 ring-blue-400/30' : 'card-hover'
                  }`}
                  onClick={() => {
                    const next = new Set(selectedLevers)
                    if (next.has(lever.id)) next.delete(lever.id)
                    else next.add(lever.id)
                    setSelectedLevers(next)
                  }}
                >
                  {/* Icon + checkbox + title row */}
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${catColour.bg} ${catColour.text}`}>
                      <LeverIcon icon={lever.icon} size={18} />
                    </div>
                    <p className="font-semibold text-text-primary text-sm leading-snug pt-0.5 flex-1">{lever.title}</p>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      onClick={e => e.stopPropagation()}
                      className="w-5 h-5 accent-blue-400 cursor-pointer"
                      aria-label={`Toggle ${lever.title}`}
                    />
                  </div>

                  {/* Description — for an agreed sale this IS the plain sentence */}
                  <p className="text-text-secondary text-sm leading-relaxed">{lever.description}</p>

                  {/* The first step. These cards presented life-changing
                      decisions (downsize, relocate, salary sacrifice) with a
                      tickbox and NOWHERE TO GO — the whole section was a
                      dead-end. Every lever now carries a real page. */}
                  {lever.link && (
                    <Link
                      to={lever.link}
                      onClick={e => e.stopPropagation()}
                      className="btn-secondary self-start gap-1.5 text-sm"
                    >
                      {lever.link_label ?? 'Take the first step'} →
                    </Link>
                  )}

                  {/* REAL sale in progress — agreed figures + editable estimates,
                      replaces the hypothetical timing slider for this property */}
                  {lever.sale_in_progress && lever.property_slug && (
                    <SaleAgreedDetails
                      slug={lever.property_slug}
                      sale={lever.sale_in_progress}
                      onSaved={() => {
                        getFinancialLevers().then(r => setLevers(r.levers)).catch(() => {})
                      }}
                    />
                  )}

                  {/* Per-lever timing slider — only meaningful for the timed lump-sum levers */}
                  {isSelected && !lever.sale_in_progress && (lever.id === 'downsize' || lever.id === 'sell_farm_avenue' || lever.id === 'sell_gables') && (
                    <div onClick={e => e.stopPropagation()}>
                      <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">
                        When? <span className="text-text-secondary normal-case font-mono">
                          {leverTiming[lever.id] ?? 0} {leverTiming[lever.id] === 1 ? 'year' : 'years'} from now
                        </span>
                      </label>
                      <input
                        type="range"
                        min={0}
                        max={20}
                        step={1}
                        value={leverTiming[lever.id] ?? 0}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10)
                          setLeverTiming(prev => ({ ...prev, [lever.id]: v }))
                        }}
                        className="w-full accent-blue-400 cursor-pointer"
                        aria-label={`Years from now to ${lever.title}`}
                      />
                      <div className="flex justify-between text-[9px] text-text-muted">
                        <span>now</span>
                        <span>10y</span>
                        <span>20y</span>
                      </div>
                    </div>
                  )}

                  {/* Impact badges row */}
                  <div className="flex flex-wrap gap-2 mt-auto pt-1">
                    {lever.status === 'sale_agreed' && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        Sale agreed — real
                      </span>
                    )}
                    {hasImpactGbp && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                        {lever.category === 'savings' || lever.category === 'investment'
                          ? `Grows to ${fmtGBPCompact(lever.impact_gbp)}`
                          : lever.category === 'tax'
                          ? `Saves ${fmtGBPCompact(lever.impact_gbp)}/yr`
                          : `Frees ${fmtGBPCompact(lever.impact_gbp)}`
                        }
                      </span>
                    )}
                    {hasImpactYears && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        {lever.impact_years} yr{lever.impact_years === 1 ? '' : 's'} sooner
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${diffConfig.bg} ${diffConfig.text}`}>
                      {diffConfig.label}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${catColour.bg} ${catColour.text} capitalize`}>
                      {lever.category}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Combined-scenario simulator footer */}
          {selectedLevers.size > 0 && (
            <div className="mt-6 bg-blue-500/5 border border-blue-400/30 rounded-xl p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <p className="font-semibold text-text-primary">
                  Combined scenario — {selectedLevers.size} lever{selectedLevers.size === 1 ? '' : 's'} selected
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      setSimRunning(true)
                      setSimError(null)
                      try {
                        const timing: Record<string, number> = {}
                        for (const id of selectedLevers) {
                          if ((id === 'downsize' || id === 'sell_farm_avenue' || id === 'sell_gables') && leverTiming[id] !== undefined) {
                            timing[id] = leverTiming[id]
                          }
                        }
                        const res = await simulateLevers(
                          Array.from(selectedLevers),
                          Object.keys(timing).length > 0 ? timing : undefined,
                        )
                        setSimulation(res)
                      } catch {
                        setSimError('Simulation failed — please try again.')
                      } finally {
                        setSimRunning(false)
                      }
                    }}
                    disabled={simRunning}
                    className="btn-primary min-h-[40px]"
                  >
                    {simRunning ? 'Computing…' : 'Simulate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSelectedLevers(new Set()); setSimulation(null); setSimError(null) }}
                    className="min-h-[40px] px-3 py-2 rounded-lg text-xs text-text-muted hover:text-text-primary"
                  >
                    Clear
                  </button>
                </div>
              </div>
              {simError && (
                <p className="text-sm text-rose-300 mb-3" role="alert">{simError}</p>
              )}
              {simulation ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-surface-card rounded-xl p-4 border border-border">
                    <p className="text-[10px] uppercase tracking-wider text-text-muted">FIRE timeline</p>
                    <p className="text-2xl font-bold text-text-primary mt-1">
                      {simulation.scenario.years_to_fire ?? '—'} <span className="text-sm text-text-muted">yrs</span>
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      vs baseline {simulation.baseline.years_to_fire ?? '—'} yrs
                      {simulation.deltas.years_saved > 0 && (
                        <span className="ml-1 text-emerald-300 font-semibold">
                          ({fmtNum(simulation.deltas.years_saved, 1)} yr sooner)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="bg-surface-card rounded-xl p-4 border border-border">
                    <p className="text-[10px] uppercase tracking-wider text-text-muted">FIRE target</p>
                    <p className="text-2xl font-bold text-text-primary mt-1">
                      {fmtGBPCompact(simulation.scenario.fire_target_gbp)}
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      vs {fmtGBPCompact(simulation.baseline.fire_target_gbp)}
                      {simulation.deltas.fire_target_reduction_gbp > 0 && (
                        <span className="ml-1 text-emerald-300 font-semibold">
                          (−{fmtGBPCompact(simulation.deltas.fire_target_reduction_gbp)})
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="bg-surface-card rounded-xl p-4 border border-border">
                    <p className="text-[10px] uppercase tracking-wider text-text-muted">Capital injected</p>
                    {(() => {
                      // timed_lump_gbp/_year are newer backend delta fields not yet in
                      // the shared api type — normalise here; all fields nullable.
                      const d = simulation.deltas as LeverSimulationResponse['deltas'] & {
                        timed_lump_gbp?: number | null
                        timed_lump_year?: number | null
                      }
                      const immediate = d.extra_lump_gbp ?? 0
                      const timed = d.timed_lump_gbp ?? 0
                      return (
                        <>
                          <p className="text-2xl font-bold text-text-primary mt-1">
                            {fmtGBPCompact(immediate + timed)}
                          </p>
                          <p className="text-xs text-text-muted mt-1">
                            {timed > 0 && (
                              <>{fmtGBPCompact(timed)} injected in year {d.timed_lump_year ?? 0} · </>
                            )}
                            + {fmtGBPCompact(d.extra_annual_savings_gbp ?? 0)}/yr savings
                            {(d.annual_drawdown_reduction_gbp ?? 0) > 0 && (
                              <> · −{fmtGBPCompact(d.annual_drawdown_reduction_gbp ?? 0)}/yr spend</>
                            )}
                          </p>
                        </>
                      )
                    })()}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-text-muted">
                  Hit Simulate to project the cumulative effect on your FIRE timeline and target pot.
                </p>
              )}
            </div>
          )}
        </Section>
      )}

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer className="text-center text-xs text-text-muted pb-4">
        Data refreshed from your live accounts. For detailed analysis, use the full Command Centre.
      </footer>
    </div>
  )
}
